import { motion, type HTMLMotionProps } from "framer-motion";
import { forwardRef } from "react";

import { cn } from "@/lib/formatters";

type Variant = "primary" | "secondary" | "ghost" | "danger";

interface ButtonProps extends HTMLMotionProps<"button"> {
  variant?: Variant;
}

const variantStyles: Record<Variant, string> = {
  primary:
    "bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20 hover:border-primary/50 hover:shadow-glow",
  secondary:
    "bg-bg-elev text-text border border-border hover:border-primary/30",
  ghost:
    "bg-transparent text-muted hover:text-text hover:bg-bg-elev/60",
  danger:
    "bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = "primary", className, children, ...props }, ref) => (
    <motion.button
      ref={ref}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 25 }}
      className={cn(
        "inline-flex items-center justify-center gap-2",
        "px-5 py-2.5 rounded-md font-mono text-sm uppercase tracking-wider",
        "transition-[background,border-color,box-shadow,color] duration-200",
        "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:transform-none",
        variantStyles[variant],
        className,
      )}
      {...props}
    >
      {children}
    </motion.button>
  ),
);

Button.displayName = "Button";
